<template>
  <div class="donations-container">
    <!-- Header Section with Glassmorphism -->
    <div class="donations-header">
      <div class="header-content">
        <div class="title-section">
          <h1 class="donations-title">
            <v-icon large class="title-icon">mdi-heart</v-icon>
            Donation History
          </h1>
          <p class="donations-subtitle">
            Track your generosity and make an impact
          </p>
        </div>
        
        <!-- Stats Cards -->
        <div class="stats-grid" v-if="profileType == 'Agency'">
          <div class="stat-card received-stat">
            <div class="stat-icon">
              <v-icon color="success">mdi-trending-up</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ receivedCount }}</div>
              <div class="stat-label">Received</div>
            </div>
          </div>
          <div class="stat-card paid-stat">
            <div class="stat-icon">
              <v-icon color="primary">mdi-trending-down</v-icon>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ paidCount }}</div>
              <div class="stat-label">Paid Out</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modern Filter Buttons -->
      <div class="filter-section" v-if="profileType == 'Agency'">
        <div class="filter-buttons">
                    <v-btn
            :class="['filter-btn', { active: filter === 'received' }]"
            @click="filter = 'received'"
            elevation="0"
          >
            <v-icon left size="20">mdi-arrow-down-circle</v-icon>
            Received
            <v-chip
              v-if="filter === 'received'"
              x-small
              color="white"
              text-color="success"
              class="ml-2"
            >
              {{ receivedCount }}
            </v-chip>
          </v-btn>
          <v-btn
            :class="['filter-btn', { active: filter === 'paid' }]"
            @click="filter = 'paid'"
            elevation="0"
          >
            <v-icon left size="20">mdi-arrow-up-circle</v-icon>
            Paid
            <v-chip
              v-if="filter === 'paid'"
              x-small
              color="white"
              text-color="primary"
              class="ml-2"
            >
              {{ paidCount }}
            </v-chip>
          </v-btn>

        </div>
      </div>
    </div>

      <div class="pagination-top" v-if="!showFullTable">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="5"
          color="primary"
          class="mb-4"
        />
      </div>
    <!-- Card Layout for Mobile, Tablet, and Laptops -->
    <div class="card-layout" v-if="!showFullTable">
      
      <div
        v-for="item in paginatedItems"
        :key="item._id"
        class="donation-card"
        @click="onSelectDonation(item)"
      >
        <!-- Card Header -->
        <div class="card-header">
          <div class="transaction-type">
            <v-icon
              :color="item.receiver._id === profile._id ? 'success' : 'error'"
              class="mr-2"
            >
              {{ item.receiver._id === profile._id ? 'mdi-arrow-down-bold' : 'mdi-arrow-up-bold' }}
            </v-icon>
            <span class="transaction-label">
              {{ item.receiver._id === profile._id ? 'Received' : 'Paid' }}
            </span>
          </div>
          <div 
            :class="[
              'transaction-amount',
              item.receiver._id === profile._id ? 'amount-received' : 'amount-paid'
            ]"
          >
            {{ formatCurrency(item.pay_in / 100) }}
          </div>
        </div>

        <!-- Card Body -->
        <div class="card-body">
          <!-- From Section -->
          <div class="transaction-section">
            <div class="section-label">From</div>
            <div class="user-info-mobile" @click.stop="goToProfile(item.payer._id, item.payer_type)">
              <editable-avatar
                :image="item.payer.midThumbnail || item.payer.thumbnail"
                :editable="false"
                :size="36"
                class="mobile-avatar"
              />
              <div class="mobile-user-details">
                <div class="mobile-user-name">
                   {{ item.payer.name? item.payer.name : item.payer.first_name + ' ' + item.payer.last_name }}
                  <v-icon v-if="item.payer.verified" x-small color="blue" class="ml-1">
                    mdi-check-decagram
                  </v-icon>
                </div>
                <div class="mobile-user-email">{{ truncateEmail(item.payer.email) }}</div>
              </div>
            </div>
          </div>

          <!-- To Section -->
          <div class="transaction-section">
            <div class="section-label">To</div>
            <div class="user-info-mobile" @click.stop="goToProfile(item.receiver._id, item.receiver_type)">
              <editable-avatar
                :image="item.receiver.midThumbnail || item.receiver.thumbnail"
                :editable="false"
                :size="36"
                class="mobile-avatar"
              />
              <div class="mobile-user-details">
                <div class="mobile-user-name">
                 {{ item.receiver.name ? item.receiver.name : item.receiver.firstName + ' ' + item.receiver.lastName }}
                  <v-icon v-if="item.receiver.verified" x-small color="blue" class="ml-1">
                    mdi-check-decagram
                  </v-icon>
                </div>
                <div class="mobile-user-email">{{ truncateEmail(item.receiver.email) }}</div>
              </div>
            </div>
          </div>

          <!-- Description -->
          <div class="transaction-section" v-if="item.description">
            <div class="section-label">Description</div>
            <div class="mobile-description">{{ item.description }}</div>
          </div>

          <!-- Causes -->
          <div class="transaction-section" v-if="item.causes && Object.keys(item.causes).length">
            <div class="section-label">Causes</div>
            <div class="mobile-causes">
              <v-chip
                v-for="(icon, key) in item.causes"
                :key="key"
                x-small
                outlined
                class="cause-chip-mobile mr-1 mb-1"
                :color="item.receiver._id === profile._id ? 'success' : 'primary'"
              >
                <site-icon :name="icon" type="Cause" :size="12" class="mr-1" />
                {{ icon }}
              </v-chip>
            </div>
          </div>
        </div>

        <!-- Card Footer -->
        <div class="card-footer">
          <div class="transaction-date">
            <v-icon small class="mr-1">mdi-calendar</v-icon>
            {{ formatDateShort(item.createdAt) }} at {{ formatTime(item.createdAt) }}
            <v-chip
              x-small
              :color="isRecent(item.createdAt) ? 'primary' : 'grey'"
              :outlined="!isRecent(item.createdAt)"
              class="ml-2"
            >
              {{ isRecent(item.createdAt) ? 'New' : formatRelativeTime(item.createdAt) }}
            </v-chip>
          </div>
          <div class="card-actions">
            <v-btn icon small @click.stop="onSelectDonation(item)" color="primary">
              <v-icon size="18">mdi-eye-outline</v-icon>
            </v-btn>
            <!-- <v-btn icon small @click.stop="downloadReceipt(item)" color="success">
              <v-icon size="18">mdi-download-outline</v-icon>
            </v-btn> -->
          </div>
        </div>
      </div>

      <!-- <div class="pagination-bottom">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="5"
          color="primary"
          class="mt-4"
        />
      </div> -->

      <!-- Loading State -->
      <div v-if="loading" class="mobile-loading">
        <v-progress-circular indeterminate color="primary" size="40" width="3" />
        <div class="loading-text">Loading donations...</div>
      </div>

      <!-- Empty State -->
      <div v-if="!loading && filteredDonations.length === 0" class="mobile-empty">
        <div class="empty-illustration">
          <v-icon size="60" color="grey lighten-2">mdi-gift-outline</v-icon>
        </div>
        <h3 class="empty-title">
          {{ filter === 'received' ? 'No donations received yet' : 'No donations made yet' }}
        </h3>
        <p class="empty-subtitle">
          {{ filter === 'received' 
              ? 'Donations you receive will appear here' 
              : 'Start making a difference - your donations will be tracked here' 
          }}
        </p>
        <v-btn color="primary" rounded class="empty-action-btn" @click="onLoadDonations">
          <v-icon left>mdi-refresh</v-icon>
          Refresh
        </v-btn>
      </div>
    </div>

    <!-- Full Table Layout (only for large screens) -->
    <div class="table-container" v-else>
      <v-data-table
        :headers="headers"
        :items="filteredDonations"
        :items-per-page="15"
        :loading="loading"
        @click:row="onSelectDonation"
        class="modern-table"
        mobile-breakpoint="0"
        :footer-props="{
          'items-per-page-options': [10, 15, 25, 50],
          'show-current-page': true,
          'show-first-last-page': true
        }"
      >
        <!-- Custom Headers -->
        <!-- <template v-slot:header="{ props }">
          <thead class="modern-header">
            <tr>
              <th
                v-for="header in props.headers"
                :key="header.value"
                :class="[
                  'modern-th',
                  header.align ? `text-${header.align}` : 'text-left'
                ]"
                :style="{ width: header.width }"
              >
                {{ header.text }}
              </th>
            </tr>
          </thead>
        </template> -->

        <!-- Sender Column -->
        <template v-slot:[`item.sender`]="{ item }">
          <div class="user-card" @click.stop="goToProfile(item.payer._id, item.payer_type)">
            <div class="user-avatar-container">
              <editable-avatar
                :image="item.payer.midThumbnail || item.payer.thumbnail"
                :editable="false"
                :size="48"
                class="user-avatar"
              />
              <div class="avatar-badge" v-if="item.payer.verified">
                <v-icon size="12" color="white">mdi-check</v-icon>
              </div>
            </div>
            <div class="user-info">
              <div class="user-name">
                {{ item.payer.name? item.payer.name : item.payer.first_name + ' ' + item.payer.last_name }}
              </div>
              <div class="user-email">
                {{ truncateEmail(item.payer.email) }}
              </div>
            </div>
          </div>
        </template>

        <!-- Receiver Column -->
        <template v-slot:[`item.receiver`]="{ item }">
          <div class="user-card" @click.stop="goToProfile(item.receiver._id, item.receiver_type)">
            <div class="user-avatar-container">
              <editable-avatar
                :image="item.receiver.midThumbnail || item.receiver.thumbnail"
                :editable="false"
                :size="48"
                class="user-avatar"
              />
              <div class="avatar-badge" v-if="item.receiver.verified">
                <v-icon size="12" color="white">mdi-check</v-icon>
              </div>
            </div>
            <div class="user-info">
              <div class="user-name">
                {{ item.receiver.name ? item.receiver.name : item.receiver.firstName + ' ' + item.receiver.lastName }}
              </div>
              <div class="user-email">
                {{ truncateEmail(item.receiver.email) }}
              </div>
            </div>
          </div>
        </template>

        <!-- Description Column -->
        <template v-slot:[`item.description`]="{ item }">
          <div class="description-column">
            <div class="description-text">
              {{ item.description || "No description provided" }}
            </div>
            <div class="causes-container" v-if="item.causes && Object.keys(item.causes).length">
              <v-chip
                v-for="(icon, key) in item.causes"
                :key="key"
                x-small
                outlined
                class="cause-chip"
                :color="item.receiver._id === profile._id ? 'success' : 'primary'"
              >
                <site-icon :name="icon" type="Cause" :size="14" class="mr-1" />
                {{ icon }}
              </v-chip>
            </div>
          </div>
        </template>

        <!-- Amount Column -->
        <template v-slot:[`item.amount`]="{ item }">
          <div class="amount-column">
            <div 
              :class="[
                'amount-value',
                item.receiver._id === profile._id ? 'amount-received' : 'amount-paid'
              ]"
            >
              <v-icon
                small
                :color="item.receiver._id === profile._id ? 'success' : 'error'"
                class="amount-icon"
              >
                {{ item.receiver._id === profile._id ? 'mdi-plus' : 'mdi-minus' }}
              </v-icon>
              {{ formatCurrency(item.pay_in / 100) }}
            </div>
            <div class="amount-type">
              {{ item.receiver._id === profile._id ? 'Received' : 'Paid' }}
            </div>
          </div>
        </template>

        <!-- Date Column -->
        <template v-slot:[`item.date`]="{ item }">
          <div class="date-column">
            <div class="date-primary">{{ formatDateShort(item.createdAt) }}</div>
            <div class="date-secondary">{{ formatTime(item.createdAt) }}</div>
            <v-chip
              x-small
              :color="isRecent(item.createdAt) ? 'primary' : 'grey'"
              :outlined="!isRecent(item.createdAt)"
              class="date-chip"
            >
              {{ isRecent(item.createdAt) ? 'New' : formatRelativeTime(item.createdAt) }}
            </v-chip>
          </div>
        </template>

        <!-- Actions Column -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions-column">
            <v-btn
              icon
              small
              @click.stop="onSelectDonation(item)"
              class="action-btn view-btn"
            >
              <v-icon size="18">mdi-eye-outline</v-icon>
            </v-btn>
            <!-- <v-btn
              icon
              small
              @click.stop="downloadReceipt(item)"
              class="action-btn download-btn"
            >
              <v-icon size="18">mdi-download-outline</v-icon>
            </v-btn> -->
          </div>
        </template>

        <!-- Loading State -->
        <template v-slot:loading>
          <div class="loading-container">
            <div class="loading-spinner">
              <v-progress-circular
                indeterminate
                color="primary"
                size="40"
                width="3"
              />
            </div>
            <div class="loading-text">Loading donations...</div>
          </div>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="empty-container">
            <div class="empty-illustration">
              <v-icon size="80" color="grey lighten-2">mdi-gift-outline</v-icon>
            </div>
            <h3 class="empty-title">
              {{ filter === 'received' ? 'No donations received yet' : 'No donations made yet' }}
            </h3>
            <p class="empty-subtitle">
              {{ filter === 'received' 
                  ? 'Donations you receive will appear here' 
                  : 'Start making a difference - your donations will be tracked here' 
              }}
            </p>
            <v-btn
              color="primary"
              rounded
              class="empty-action-btn"
              @click="onLoadDonations"
              :loading="loading"
            >
              <v-icon left>mdi-refresh</v-icon>
              Refresh
            </v-btn>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- Enhanced Snackbar -->
    <v-snackbar
      v-model="snackbar"
      :color="errorType"
      top
      right
      timeout="6000"
      class="modern-snackbar"
    >
      <div class="snackbar-content">
        <v-icon left color="white">
          {{ errorType === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
        </v-icon>
        {{ errorMessage }}
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar = false" color="white">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";
import dateFormat from "dateformat";

export default {
  components: { EditableAvatar },
  data() {
    return {
      loading: false,
      errorMessage: null,
      errorType: "error",
      snackbar: false,
      currentPage: 1,
      itemsPerPage: 5,
      headers: [
        { text: "From", value: "sender", sortable: false, width: "240px" },
        { text: "To", value: "receiver", sortable: false, width: "240px" },
        // { text: "Description", value: "description", sortable: false, width: "280px" },
        { text: "Amount", value: "amount", align: "right", width: "160px" },
        { text: "Date", value: "date", width: "180px" },
        { text: "Actions", value: "actions", sortable: false, align: "center", width: "120px" }
      ],
      filter: "paid"
    };
  },
  computed: {
    ...mapGetters({
      donations: "donation/getAllDonations",
      profileType: "auth/getType",
      profile: "auth/getProfile"
    }),
    showFullTable() {
      return this.$vuetify.breakpoint.width >= 1440;
    },
    filteredDonations() {
      if (this.filter === "received") {
        return this.donations.filter(d => d.receiver._id === this.profile._id);
      } else if (this.filter === "paid") {
        return this.donations.filter(d => d.payer._id === this.profile._id);
      }
      return this.donations;
    },
    paidCount() {
      return this.donations.filter(d => d.payer._id === this.profile._id).length;
    },
    receivedCount() {
      return this.donations.filter(d => d.receiver._id === this.profile._id).length;
    },
    totalPages() {
      return Math.ceil(this.filteredDonations.length / this.itemsPerPage);
    },
    paginatedItems() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredDonations.slice(start, end);
    }
  },
  watch: {
    '$vuetify.breakpoint.width': {
      immediate: true,
      handler(width) {
        if (width < 600) {
          this.itemsPerPage = 5; // Mobile
        } else if (width < 960) {
          this.itemsPerPage = 6; // Tablet
        } else if (width < 1440) {
          this.itemsPerPage = 8; // Laptop
        }
      }
    }
  },
  methods: {
    ...mapActions({
      fetchAllDonations: "donation/fetchAllDonations"
    }),

    onSelectDonation(donation) {
      this.$router.push({
        name: "donation-details",
        query: { _id: donation._id }
      });
    },

    downloadReceipt(item) {
      console.log("Download receipt for", item._id);
      this.showMessage("Receipt download started", "success");
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(amount);
    },

    formatDateShort(date) {
      return dateFormat(date, "mmm d, yyyy");
    },

    formatTime(date) {
      return dateFormat(date, "h:MM TT");
    },

    formatRelativeTime(date) {
      const now = new Date();
      const diffInDays = Math.floor((now - new Date(date)) / (1000 * 60 * 60 * 24));

      if (diffInDays === 0) return "Today";
      if (diffInDays === 1) return "Yesterday";
      if (diffInDays < 7) return `${diffInDays}d ago`;
      if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`;
      return `${Math.floor(diffInDays / 30)}m ago`;
    },

    isRecent(date) {
      return new Date() - new Date(date) < 24 * 60 * 60 * 1000;
    },

    truncateEmail(email) {
      if (!email) return '';
      if (email.length <= 20) return email;
      return email.substring(0, 17) + '...';
    },

    showMessage(message, type = "error") {
      this.errorMessage = message;
      this.errorType = type;
      this.snackbar = true;
    },

    onLoadDonations() {
      this.loading = true;
      this.fetchAllDonations()
        .then(() => {
          this.loading = false;
          this.showMessage("Donations loaded successfully", "success");
        })
        .catch(error => {
          this.loading = false;
          this.showMessage(
            error.response?.data?.message || "Failed to load donations"
          );
        });
    },

    goToProfile(id, type) {
      this.$router.push({
        name: type.toLowerCase() + "-profile",
        params: { id }
      });
    },

    handleSuccessfulPayment() {
      const paymentIntent = this.$route.query.payment_intent;
      if (paymentIntent) {
        this.loading = true;
        this.$store
          .dispatch("donation/recordPaymentSuccess", {
            paymentIntentId: paymentIntent
          })
          .then(() => {
            this.showMessage("Payment recorded successfully", "success");
          })
          .catch(error => {
            this.showMessage("Failed to record payment",error);
          })
          .finally(() => {
            this.loading = false;
            this.$router.replace({ name: "donation-history" });
          });
      }
    }
  },
  mounted() {
    this.onLoadDonations();
    if (this.$route.query.payment_success) {
      this.handleSuccessfulPayment();
    }
  }
};
</script>

<style scoped lang="scss">
.donations-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px;
  // background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
}

.donations-header {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 32px;
  margin-bottom: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(31, 38, 135, 0.37);

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;

    .title-section {
      .donations-title {
        display: flex;
        align-items: center;
        font-size: 2.5rem;
        font-weight: 800;
        color: white;
        margin: 0 0 8px;
        letter-spacing: -0.02em;

        .title-icon {
          margin-right: 16px;
          background: linear-gradient(135deg,#084468,#3b7ca1);
          border-radius: 20%;
          color: white;
          padding: 8px;
        }
      }

      .donations-subtitle {
        font-size: 1.1rem;
        color: rgba(255, 255, 255, 0.8);
        margin: 0;
        font-weight: 400;
      }
    }

    .stats-grid {
      display: flex;
      gap: 16px;

      .stat-card {
        // background: rgba(255, 255, 255, 0.15);
        backdrop-filter: blur(10px);
        border-radius: 16px;
        padding: 20px;
        min-width: 120px;
        border: 1px solid rgba(255, 255, 255, 0.2);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
        }

        .stat-icon {
          margin-bottom: 8px;
        }

        .stat-number {
          font-size: 2rem;
          font-weight: 700;
          color: white;
          line-height: 1;
        }

        .stat-label {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.7);
          font-weight: 500;
        }
      }
    }
  }

  .filter-section {
    .filter-buttons {
      display: flex;
      gap: 8px;
      background: rgba(255, 255, 255, 0.1);
      padding: 4px;
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.2);

      .filter-btn {
        flex: 1;
        height: 48px;
        border-radius: 12px;
        text-transform: none;
        font-weight: 600;
        letter-spacing: 0.02em;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        background: transparent;
        color: rgba(255, 255, 255, 0.7);

        &.active {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          transform: scale(1.02);
        }

        &:hover:not(.active) {
          background: rgba(255, 255, 255, 0.05);
          color: rgba(255, 255, 255, 0.9);
        }
      }
    }
  }
}

/* Card Layout Styles */
.card-layout {
  display: grid;
  gap: 16px;
  padding: 8px;
}

.donation-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }
}

/* Responsive Grid for Cards */
@media (max-width: 599px) {
  .card-layout {
    grid-template-columns: 1fr;
  }
}

@media (min-width: 600px) and (max-width: 959px) {
  .card-layout {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 960px) and (max-width: 1199px) {
  .card-layout {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 1200px) and (max-width: 1439px) {
  .card-layout {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Card Content Styles */
.card-header {
  background: linear-gradient(135deg, #f8f9ff 0%, #f1f3ff 100%);
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  .transaction-type {
    display: flex;
    align-items: center;

    .transaction-label {
      font-weight: 600;
      font-size: 0.9rem;
      color: #4a5568;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
  }

  .transaction-amount {
    font-weight: 800;
    font-size: 1.2rem;

    &.amount-received {
      color: #10b981;
    }

    &.amount-paid {
      color: #ef4444;
    }
  }
}

.card-body {
  padding: 20px;

  .transaction-section {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    .section-label {
      font-size: 0.75rem;
      font-weight: 600;
      color: #718096;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      margin-bottom: 8px;
    }

    .user-info-mobile {
      display: flex;
      align-items: center;
      padding: 8px;
      border-radius: 12px;
      transition: all 0.2s ease;
      cursor: pointer;

      &:hover {
        background: rgba(102, 126, 234, 0.05);
      }

      .mobile-avatar {
        margin-right: 12px;
        border: 2px solid #fff;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .mobile-user-details {
        flex: 1;

        .mobile-user-name {
          font-weight: 600;
          font-size: 0.9rem;
          color: #2d3748;
          display: flex;
          align-items: center;
          margin-bottom: 4px;
        }

        .mobile-user-email {
          font-size: 0.8rem;
          color: #718096;
        }
      }
    }

    .mobile-description {
      font-size: 0.875rem;
      color: #4a5568;
      line-height: 1.5;
      background: #f7fafc;
      padding: 12px;
      border-radius: 8px;
      border-left: 3px solid #667eea;
    }

    .mobile-causes {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;

      .cause-chip-mobile {
        font-size: 0.7rem;
        height: 24px;
        font-weight: 500;
      }
    }
  }
}

.card-footer {
  background: #f7fafc;
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid rgba(0, 0, 0, 0.05);

  .transaction-date {
    display: flex;
    align-items: center;
    font-size: 0.8rem;
    color: #718096;
    flex: 1;
  }

  .card-actions {
    display: flex;
    gap: 8px;

    .v-btn {
      transition: all 0.2s ease;

      &:hover {
        transform: scale(1.1);
      }
    }
  }
}

/* Table Styles (for large screens only) */
.table-container {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.modern-table {
  .modern-header {
    background: linear-gradient(135deg, #f8f9ff 0%, #f1f3ff 100%);

    .modern-th {
      font-weight: 700;
      color: #2d3748;
      font-size: 0.875rem;
      letter-spacing: 0.05em;
      text-transform: uppercase;
      padding: 20px 16px;
      border-bottom: 2px solid #e2e8f0;
    }
  }

  ::v-deep tbody tr {
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%);
      transform: translateY(-1px);
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
    }

    td {
      padding: 20px 16px;
      border-bottom: 1px solid #f0f4f8;
    }
  }
}

.user-card {
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.02);
  }

  .user-avatar-container {
    position: relative;
    margin-right: 16px;

    .user-avatar {
      border: 3px solid #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transition: all 0.3s ease;
    }

    .avatar-badge {
      position: absolute;
      bottom: -2px;
      right: -2px;
      background: #3b82f6;
      border-radius: 50%;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 2px solid white;
    }
  }

  .user-info {
    .user-name {
      font-weight: 600;
      font-size: 0.95rem;
      color: #2d3748;
      margin-bottom: 4px;
      line-height: 1.2;
    }

    .user-email {
      font-size: 0.8rem;
      color: #718096;
      font-weight: 400;
    }
  }
}

.description-column {
  .description-text {
    font-size: 0.875rem;
    color: #4a5568;
    line-height: 1.5;
    margin-bottom: 8px;
    font-weight: 400;
  }

  .causes-container {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;

    .cause-chip {
      font-size: 0.75rem;
      height: 24px;
      font-weight: 500;
    }
  }
}

.amount-column {
  text-align: right;

  .amount-value {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-weight: 700;
    font-size: 1.1rem;
    margin-bottom: 4px;

    .amount-icon {
      margin-right: 6px;
    }

    &.amount-received {
      color: #10b981;
    }

    &.amount-paid {
      color: #ef4444;
    }
  }

  .amount-type {
    font-size: 0.75rem;
    color: #718096;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
}

.date-column {
  .date-primary {
    font-weight: 600;
    font-size: 0.875rem;
    color: #2d3748;
    margin-bottom: 4px;
  }

  .date-secondary {
    font-size: 0.75rem;
    color: #718096;
    margin-bottom: 8px;
  }

  .date-chip {
    font-size: 0.7rem;
    font-weight: 600;
    height: 20px;
  }
}

.actions-column {
  display: flex;
  gap: 8px;
  justify-content: center;

  .action-btn {
    transition: all 0.2s ease;
    border-radius: 8px;

    &:hover {
      transform: scale(1.1);
    }

    &.view-btn:hover {
      background: rgba(59, 130, 246, 0.1);
      color: #3b82f6;
    }

    &.download-btn:hover {
      background: rgba(16, 185, 129, 0.1);
      color: #10b981;
    }
  }
}

/* Pagination Styles */
.pagination-top, .pagination-bottom {
  display: flex;
  justify-content: center;
  width: 100%;
}

/* Loading States */
.loading-container, .mobile-loading {
  padding: 60px 20px;
  text-align: center;

  .loading-spinner, .v-progress-circular {
    margin-bottom: 20px;
  }

  .loading-text {
    font-size: 1rem;
    color: #718096;
    font-weight: 500;
  }
}

/* Empty States */
.empty-container, .mobile-empty {
  padding: 60px 20px;
  text-align: center;

  .empty-illustration {
    margin-bottom: 20px;
  }

  .empty-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #2d3748;
    margin: 20px 0 12px;
  }

  .empty-subtitle {
    font-size: 1rem;
    color: #718096;
    margin-bottom: 24px;
  }

  .empty-action-btn {
    font-weight: 600;
    letter-spacing: 0.02em;
  }
}

/* Snackbar Styles */
.modern-snackbar {
  .snackbar-content {
    display: flex;
    align-items: center;
    font-weight: 500;
  }
}

/* Hide table on smaller screens */
@media (max-width: 1439px) {
  .table-container {
    display: none;
  }
}

/* Show only card layout on smaller screens */
@media (min-width: 1440px) {
  .card-layout {
    display: none;
  }
}

/* Responsive adjustments for header */
@media (max-width: 1024px) {
  .donations-header {
    .header-content {
      flex-direction: column;
      gap: 24px;

      .stats-grid {
        width: 100%;
        flex-direction: row;
        justify-content: space-around;
      }
    }
  }
}

@media (max-width: 768px) {
  .donations-container {
    padding: 16px;
  }

  .donations-header {
    padding: 20px;

    .header-content .title-section .donations-title {
      font-size: 1.75rem;
      flex-direction: column;
      text-align: center;

      .title-icon {
        margin-right: 0;
        margin-bottom: 8px;
      }
    }
  }
}
</style>